import React, { useState, useEffect } from 'react';
import { Form, Button, Typography, Row, Col, Upload, Input, Select, Space, Tooltip, App as AntApp } from 'antd';
import { SaveOutlined, UploadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ControlSelect from '../controls/ControlSelect';
import AddButton from '../controls/AddButton';
import AddModal from '../controls/AddModal';
import api from '../services/api';

const { Title } = Typography;

const ProductoNuevo = () => {
  const { modal: antModal } = AntApp.useApp();
  const [form] = Form.useForm();

  const selectedCategoria    = Form.useWatch('ddCategoria', form);
  const selectedTipoPlanDesc = Form.useWatch('ddTipoPlanDescuento', form);
  const showPlanDesc    = Number(selectedCategoria) === 6;
  const showPresentacion = Number(selectedTipoPlanDesc) === 2;

  const wNombre      = Form.useWatch('txtNombre', form);
  const wUnidadBase  = Form.useWatch('ddUnidadBase', form);
  const wCentroCosto = Form.useWatch('ddCentroCosto', form);
  const wPlanDesc    = Form.useWatch('txtPlanDescuento', form);
  const wPresentacion = Form.useWatch('ddPresentacion', form);

  const isSaveDisabled = (() => {
    if (!wNombre || !wUnidadBase || !wCentroCosto) return true;
    if (showPlanDesc) {
      if (!selectedTipoPlanDesc) return true;
      if (!wPlanDesc || String(wPlanDesc).trim() === '') return true;
      if (Number(selectedTipoPlanDesc) === 2 && !wPresentacion) return true;
    }
    return false;
  })();

  const [categorias, setCategorias]   = useState([]);
  const [marcas, setMarcas]           = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [addModal, setAddModal]       = useState({ visible: false, tipo: '' });
  const [fileList, setFileList]       = useState([]);
  const [fileToUpload, setFileToUpload] = useState(null);

  // Todas las unidades agrupadas por tipo  → para el selector "Unidad de inventario"
  const [unidadesAgrupadas, setUnidadesAgrupadas] = useState([]);
  const [unidadesLoading, setUnidadesLoading]     = useState(false);
  // Mapa unitId → tipoMedidaId  (para armar el payload sin un campo extra en el form)
  const [unidadTipoMap, setUnidadTipoMap]         = useState({});
  // Mapa unitId → factorBase     (para presentaciones del plan de consumo)
  const [unidadFactorMap, setUnidadFactorMap]     = useState({});

  // Presentaciones (visible si Tipo de descuento = 2) — reutiliza las mismas unidades
  const [presentaciones, setPresentaciones]               = useState([]);
  const [presentacionLoading, setPresentacionLoading]     = useState(false);
  const [presentacionFactorById, setPresentacionFactorById] = useState({});

  useEffect(() => {
    api.get('/Categoria/active')
      .then(res => {
        const vals = res.data.$values || res.data || [];
        setCategorias(vals.map(c => ({ value: c.id, label: c.nombre })));
      }).catch(console.error);

    api.get('/Marca/active')
      .then(res => {
        const vals = res.data.$values || res.data || [];
        setMarcas(vals.map(m => ({ value: m.id, label: m.nombre })));
      }).catch(console.error);

    api.get('/CentroCosto/activos')
      .then(res => {
        const vals = res.data.$values || res.data || [];
        setCentrosCosto(vals.map(c => ({ value: c.id, label: c.nombre })));
      }).catch(console.error);

    // Cargar tipos y unidades en paralelo para construir el selector agrupado
    const cargarUnidades = async () => {
      try {
        setUnidadesLoading(true);
        const [tipoRes, unidadRes] = await Promise.all([
          api.get('/TipoMedida'),
          api.get('/UnidadMedida'),
        ]);

        const tipos    = tipoRes.data.$values   || tipoRes.data   || [];
        const unidades = unidadRes.data.$values || unidadRes.data || [];

        // Mapa tipo id → nombre
        const tipoNombreMap = {};
        tipos.forEach(t => { tipoNombreMap[t.id] = t.nombre; });

        // Mapas auxiliares
        const tipoMap   = {};
        const factorMap = {};
        const factorById = {};
        unidades.forEach(u => {
          tipoMap[u.id]   = u.idTipoMedida;
          factorMap[u.id] = u.factorBase ?? 1;
          factorById[u.id] = u.factorBase ?? 1;
        });
        setUnidadTipoMap(tipoMap);
        setUnidadFactorMap(factorMap);
        setPresentacionFactorById(factorById);

        // Agrupar por tipo para el Select agrupado
        const grupos = {};
        unidades
          .filter(u => u.estado == null || u.estado === 1)
          .forEach(u => {
            const tipoId     = u.idTipoMedida ?? 0;
            const tipoNombre = tipoNombreMap[tipoId] || 'Otros';
            if (!grupos[tipoId]) grupos[tipoId] = { label: tipoNombre, options: [] };
            const abrev = u.abreviatura ? ` (${u.abreviatura})` : '';
            grupos[tipoId].options.push({ value: u.id, label: `${u.nombre}${abrev}` });
          });

        setUnidadesAgrupadas(Object.values(grupos));
      } catch (err) {
        console.error(err);
      } finally {
        setUnidadesLoading(false);
      }
    };

    cargarUnidades();
  }, []);

  // Presentaciones: carga cuando el usuario elige "Tipo descuento = Peso (2)"
  useEffect(() => {
    if (Number(selectedTipoPlanDesc) !== 2) {
      setPresentaciones([]);
      form.setFieldsValue({ ddPresentacion: undefined });
      return;
    }
    // Reutilizamos las unidades ya cargadas en unidadesAgrupadas
    const flat = unidadesAgrupadas.flatMap(g => g.options);
    if (flat.length) {
      setPresentaciones(
        flat.map(u => ({
          value: u.value,
          label: `${u.label} — factor: ${unidadFactorMap[u.value] ?? 1}`,
        }))
      );
    } else {
      // Si aún no cargaron, traerlas
      setPresentacionLoading(true);
      api.get('/UnidadMedida')
        .then(res => {
          const vals = res.data.$values || res.data || [];
          setPresentaciones(vals.map(p => ({
            value: p.id,
            label: `${p.nombre} (${p.factorBase ?? 1})`,
          })));
          const map = {};
          vals.forEach(p => { map[p.id] = p.factorBase ?? 1; });
          setPresentacionFactorById(map);
        })
        .catch(console.error)
        .finally(() => setPresentacionLoading(false));
    }
  }, [selectedTipoPlanDesc, unidadesAgrupadas, unidadFactorMap, form]);

  const showModal   = (tipo) => setAddModal({ visible: true, tipo });
  const handleCancel = () => setAddModal({ visible: false, tipo: '' });

  const handleFileChange = ({ fileList: newList }) => {
    const latest = newList.slice(-1)[0];
    setFileList(newList.slice(-1));
    setFileToUpload(latest?.originFileObj || null);
  };

  const handleAdd = async (values) => {
    try {
      const payload    = { nombre: values.nombre, estado: 1 };
      const endpoint   = addModal.tipo === 'categoria' ? '/Categoria' : '/Marca';
      await api.post(endpoint, payload);
      const getEndpoint = addModal.tipo === 'categoria' ? '/Categoria/active' : '/Marca/active';
      const res = await api.get(getEndpoint);
      const vals = res.data.$values || res.data;
      const setter = addModal.tipo === 'categoria' ? setCategorias : setMarcas;
      setter(vals.map(x => ({ value: x.id, label: x.nombre })));
      handleCancel();
    } catch (err) {
      console.error(err);
    }
  };

  const onFinish = async (values) => {
    try {
      if (!values.ddUnidadBase) {
        antModal.error({ title: 'Validación', content: 'Debes seleccionar la Unidad de inventario.' });
        return;
      }
      if (!values.ddCentroCosto) {
        antModal.error({ title: 'Validación', content: 'Debes seleccionar el Centro de costo.' });
        return;
      }

      const isAlimentos = Number(values.ddCategoria) === 6;
      if (isAlimentos) {
        if (!values.ddTipoPlanDescuento) {
          antModal.error({ title: 'Validación', content: 'Debes seleccionar el Tipo de descuento.' });
          return;
        }
        if (!values.txtPlanDescuento || String(values.txtPlanDescuento).trim() === '') {
          antModal.error({ title: 'Validación', content: 'Debes diligenciar el campo Descuento inventario.' });
          return;
        }
        if (Number(values.ddTipoPlanDescuento) === 2) {
          if (!values.ddPresentacion) {
            antModal.error({ title: 'Validación', content: 'Debes seleccionar la Presentación.' });
            return;
          }
          if (isNaN(parseFloat(values.txtPlanDescuento))) {
            antModal.error({ title: 'Validación', content: 'El campo Descuento inventario debe ser numérico para tipo Peso.' });
            return;
          }
        }
      }

      // tipoMedidaId se deriva de la unidad seleccionada
      const unidadBaseId   = parseInt(values.ddUnidadBase, 10);
      const tipoMedidaId   = unidadTipoMap[unidadBaseId] ?? 0;

      const productoPayload = {
        id: 0,
        codProducto:    values.txtCodigo ? parseInt(values.txtCodigo, 10) : 0,
        categoria:      values.ddCategoria ? parseInt(values.ddCategoria, 10) : 0,
        nombre:         values.txtNombre,
        codigoBarras:   values.txtCodbarras || '',
        marca:          values.ddMarca ? parseInt(values.ddMarca, 10) : 0,
        tipoMedidaId,
        idUnidadMedida: unidadBaseId,
        unidadBaseId,
        idCentrosCosto: parseInt(values.ddCentroCosto, 10),
        estado: 1,
      };

      const { data: producto } = await api.post('/Producto', productoPayload);

      const productoId = Number(
        (producto && (producto.id ?? producto.Id ?? producto.idProducto ?? producto.productoId)) ?? 0
      );
      if (!productoId) {
        antModal.error({ title: 'Error', content: 'No fue posible obtener el Id del producto desde el servidor.' });
        return;
      }

      if (fileToUpload) {
        const formData = new FormData();
        formData.append('file', fileToUpload);
        await api.post(`/ProductoImagen/${productoId}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (isAlimentos) {
        const tipoPlan = Number(values.ddTipoPlanDescuento);
        const infoStr  = String(values.txtPlanDescuento).trim();
        const infoNum  = Number(infoStr);
        await api.post('/PlanConsumo', {
          id: 0,
          nombre:        `${values.txtNombre} ${infoStr}`.trim(),
          tipo:          tipoPlan,
          estado:        1,
          peso:          tipoPlan === 2 ? (infoNum * Number(presentacionFactorById[values.ddPresentacion] || 1)) : null,
          numDias:       tipoPlan === 1 ? (parseInt(infoStr, 10) || 0) : null,
          fechaCreacion: new Date().toISOString(),
          idProducto:    productoId,
        });
      }

      form.resetFields();
      setFileList([]);
      setFileToUpload(null);
      antModal.success({ title: 'Éxito', content: 'El producto se guardó correctamente.' });
    } catch (error) {
      console.error(error);
      antModal.error({ title: 'Error', content: 'Hubo un problema al guardar el producto.' });
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ width: '100%', maxWidth: 900 }}
      >
        <Title level={4} style={{ color: '#1890ff', textAlign: 'center' }}>
          Agregar producto
        </Title>

        {/* Nombre y Código de barras */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="txtNombre"
              label="Nombre"
              rules={[{ required: true, message: 'Por favor ingresa el nombre del producto' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="txtCodbarras" label="Código de barras">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          {/* Categoría */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Categoría">
              <Space>
                <Form.Item
                  name="ddCategoria"
                  noStyle
                  rules={[{ required: true, message: 'Debes seleccionar una categoría' }]}
                >
                  <ControlSelect
                    options={categorias}
                    placeholder="Seleccionar"
                    showSearch
                    optionFilterProp="label"
                    style={{ flex: 1 }}
                  />
                </Form.Item>
                <AddButton onClick={() => showModal('categoria')} />
              </Space>
            </Form.Item>
          </Col>

          {/* Marca */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Marca">
              <Space>
                <Form.Item
                  name="ddMarca"
                  noStyle
                  rules={[{ required: true, message: 'Debes seleccionar una marca' }]}
                >
                  <ControlSelect
                    options={marcas}
                    placeholder="Seleccionar"
                    showSearch
                    optionFilterProp="label"
                    style={{ flex: 1 }}
                  />
                </Form.Item>
                <AddButton onClick={() => showModal('marca')} />
              </Space>
            </Form.Item>
          </Col>

          {/* Unidad de inventario — selector agrupado por tipo */}
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label={
                <Space size={4}>
                  Unidad de inventario
                  <Tooltip title="Unidad en que se lleva el inventario. Ej: el maíz se almacena en Kg aunque se compre por Bultos.">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
            >
              <Form.Item
                name="ddUnidadBase"
                noStyle
                rules={[{ required: true, message: 'Debes seleccionar la unidad de inventario' }]}
              >
                <Select
                  options={unidadesAgrupadas}
                  placeholder="Seleccionar unidad"
                  showSearch
                  loading={unidadesLoading}
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form.Item>
          </Col>

          {/* Código interno */}
          <Col xs={24} sm={12} md={4}>
            <Form.Item name="txtCodigo" label="Código">
              <Input />
            </Form.Item>
          </Col>

          {/* Centro de costo */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="Centro de costo">
              <Form.Item
                name="ddCentroCosto"
                noStyle
                rules={[{ required: true, message: 'Debes seleccionar el Centro de costo' }]}
              >
                <ControlSelect
                  options={centrosCosto}
                  placeholder="Seleccionar"
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form.Item>
          </Col>

          {/* Campos visibles solo cuando categoría = 6 (Alimentos) */}
          {showPlanDesc && (
            <>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="ddTipoPlanDescuento" label="Tipo de descuento">
                  <ControlSelect
                    options={[
                      { value: 1, label: 'Diario' },
                      { value: 2, label: 'Peso' },
                    ]}
                    placeholder="Seleccionar"
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>

              {showPresentacion && (
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="ddPresentacion" label="Presentación">
                    <ControlSelect
                      options={presentaciones}
                      placeholder="Seleccionar"
                      optionFilterProp="label"
                      loading={presentacionLoading}
                    />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} md={6}>
                <Form.Item name="txtPlanDescuento" label="Descuento inventario">
                  <Input placeholder="Ej: 10 (kg/día) o 30 (días)" />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        {/* Imagen */}
        <Form.Item label="Imagen del producto" style={{ marginTop: 16 }}>
          <Upload
            accept="image/*"
            capture="environment"
            listType="picture"
            fileList={fileList}
            beforeUpload={() => false}
            onChange={handleFileChange}
          >
            <Button icon={<UploadOutlined />}>Capturar / Seleccionar</Button>
          </Upload>
        </Form.Item>

        {/* Guardar */}
        <Form.Item style={{ textAlign: 'center', marginTop: 32 }}>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} disabled={isSaveDisabled}>
            Guardar
          </Button>
        </Form.Item>

        <AddModal
          visible={addModal.visible}
          title={addModal.tipo === 'categoria' ? 'Agregar Categoría' : 'Agregar Marca'}
          onCancel={handleCancel}
          onAdd={handleAdd}
        />
      </Form>
    </div>
  );
};

export default ProductoNuevo;
